import * as yup from 'yup';
import { useTranslate } from '@tolgee/react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { UnionFragment } from '@amf/shared/types/graphql';
import TextField from '@amf/shared/components/form/TextField';
import CheckboxField from '@amf/shared/components/form/CheckboxField';
import SelectField from '@amf/shared/components/form/SelectField';
import TextArea from '@amf/shared/components/form/TextArea';
import Button from '@amf/shared/components/button/Button';

const schema = yup.object({
  name: yup
    .string()
    .required('join.player.form.name.required')
    .max(64, 'join.player.form.name.max'),
  identification: yup
    .string()
    .required('join.player.form.identification.required')
    .max(11, 'join.player.form.identification.invalid')
    .min(8, 'join.player.form.identification.invalid'),
  foreign: yup.boolean(),
  union: yup.string().required('join.player.form.union.required'),
  email: yup
    .string()
    .email('join.player.form.email.invalid')
    .required('join.player.form.email.required'),
  phone: yup
    .string()
    .matches(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g, 'contact.form.phone.invalid')
    .required('contact.form.phone.required'),
  note: yup
    .string()
    .nullable()
    .transform((_, value) => (value === '' ? null : value))
    .max(512, 'join.player.form.note.max'),
});

export interface JoinPlayerFormData {
  name: string;
  identification: string;
  foreign: boolean;
  union: string;
  email: string;
  phone: string;
  note: string | null;
}

interface JoinPlayerFormProps {
  unions: UnionFragment[];
  values: JoinPlayerFormData;
  onSubmit: (values: JoinPlayerFormData) => Promise<void>;
}

function JoinPlayerForm({ values, onSubmit, unions }: JoinPlayerFormProps) {
  const { t } = useTranslate('malyfotbal');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [sending, setSending] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<JoinPlayerFormData>({
    resolver: yupResolver(schema),
    defaultValues: values,
  });

  const verifyReCaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    await executeRecaptcha('contactForm');
  }, [executeRecaptcha]);

  useEffect(() => {
    verifyReCaptcha();
  }, [verifyReCaptcha]);

  const saveForm = useCallback(
    async (data: JoinPlayerFormData) => {
      setSending(true);
      await onSubmit(data);
      setSending(false);
    },
    [onSubmit],
  );

  return (
    <div className='Form'>
      <form onSubmit={handleSubmit(saveForm)}>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.player.form.name')}
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />
          </div>
          <div className='Form__col'>
            <Controller
              name='identification'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.player.form.identification')}
                  error={errors.identification?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.player.form.email')}
                  error={errors.email?.message}
                  type='email'
                  {...field}
                />
              )}
            />
          </div>
          <div className='Form__col'>
            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.player.form.phone')}
                  error={errors.phone?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='foreign'
              control={control}
              render={({ field }) => (
                <CheckboxField type='checkbox' label={t('join.player.form.foreign')} {...field} />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='union'
              control={control}
              render={({ field }) => (
                <SelectField
                  label={t('join.player.form.union')}
                  error={errors.union?.message}
                  className='Form__field'
                  {...field}
                >
                  <option value={''}>{t('join.player.form.union.choose')}</option>
                  {unions.map(({ id, name }) => (
                    <option key={id} value={name}>
                      {name}
                    </option>
                  ))}
                </SelectField>
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='note'
              control={control}
              render={({ field }) => (
                <TextArea
                  className='Form__field'
                  label={t('join.player.form.note')}
                  error={errors.note?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Button type='submit' variant='blue' disabled={sending}>
              {t('join.player.form.send')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default JoinPlayerForm;
